import { inject, Injectable, signal } from '@angular/core';
import { catchError, Observable, of, shareReplay, tap, throwError } from 'rxjs';
import { Release } from '../../models/release';
import { ReleaseService } from '../release/release.service';

/**
 * The release the app header is currently showing, and the list behind the header's
 * release switcher (PBI 55102 AC3).
 *
 * Two writers, deliberately: routes that resolve a release publish it from the shell
 * on every navigation, and pages that pick their own release — Insights — publish
 * theirs once loaded. The release list is cached here so a page that has already
 * fetched it (Insights) spares the switcher a second request.
 */
@Injectable({ providedIn: 'root' })
export class ReleaseContextService {
  private releaseService = inject(ReleaseService);

  private currentSig = signal<Release | null>(null);
  private releasesSig = signal<Release[]>([]);

  /** Release being viewed, or null on pages with no release context. */
  readonly current = this.currentSig.asReadonly();

  /** Everything the switcher can offer; empty until something loads it. */
  readonly releases = this.releasesSig.asReadonly();

  private inFlight: Observable<Release[]> | null = null;

  setCurrent(release: Release | null): void {
    this.currentSig.set(release);
  }

  /** Hand over a list a page already fetched, so the switcher reuses it. */
  setReleases(releases: Release[]): void {
    this.releasesSig.set(releases);
  }

  /**
   * The release list, fetched at most once. Callers that arrive while a request is
   * in flight share it; a failure is not cached, so the next open can retry.
   */
  loadReleases(): Observable<Release[]> {
    const cached = this.releasesSig();
    if (cached.length) return of(cached);

    this.inFlight ??= this.releaseService.getReleases().pipe(
      tap((releases) => {
        this.setReleases(releases);
        this.inFlight = null;
      }),
      catchError((err) => {
        this.inFlight = null;
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.inFlight;
  }
}
